import { ref } from 'vue';
import { executeApiRequest } from '~/utils/api';

// Create shared state across components
const userDetails = ref(null);
const userOrders = ref([]);
const userOrder = ref(null);
const userAddresses = ref([]);
const userExercises = ref([]);
const userWorkouts = ref([]);

/**
 * Composable for fetching and managing user data
 * This replaces direct calls to $auth API functions
 */
export const useUserData = () => {
  /**
   * Helper function to update blackrollCustomer nested properties in userDetails
   */
  const updateBlackrollCustomerProperty = (propertyName, data) => {
    if (userDetails.value && userDetails.value.blackrollCustomer) {
      userDetails.value.blackrollCustomer[propertyName] = data;
    }
  };

  // Get user profile details
  const fetchUserDetails = async () => {
    return await executeApiRequest(async () => {
      const { data } = await useFetch('/api/user/details');
      userDetails.value = data.value.data;
      return data.value.data;
    }, 'Failed to fetch user details');
  };

  // Get user orders
  const fetchUserOrders = async () => {
    return await executeApiRequest(async () => {
      const { data } = await useFetch('/api/user/orders');
      userOrders.value = data.value.data || [];
      return data.value.data;
    }, 'Failed to fetch orders');
  };

  // Get a single order by number
  const fetchUserOrder = async (orderNumber) => {
    if (!orderNumber) {
      throw new Error('Order number is required');
    }

    return await executeApiRequest(async () => {
      const { data } = await useFetch(`/api/user/order/${encodeURIComponent(orderNumber)}`);
      userOrder.value = data.value.data;
      return data.value.data;
    }, 'Failed to fetch order details');
  };

  // Get user addresses
  const fetchUserAddresses = async () => {
    return await executeApiRequest(async () => {
      const { data } = await useFetch('/api/user/addresses');
      userAddresses.value = data.value.data || [];
      return data.value.data;
    }, 'Failed to fetch addresses');
  };

  // Update user profile
  const updateUserProfile = async (profileData) => {
    return await executeApiRequest(async () => {
      const { data } = await useFetch('/api/user/profile', {
        method: 'PUT',
        body: profileData,
      });
      userDetails.value = data.value.data;
      return data.value.data;
    }, 'Failed to update profile');
  };

  // Add/update address
  const updateAddress = async (addressData, addressIndex = null) => {
    return await executeApiRequest(async () => {
      const { data } = await useFetch('/api/user/address', {
        method: 'PUT',
        body: { address: addressData, index: addressIndex },
      });
      userAddresses.value = data.value.data || [];
      return data.value.data;
    }, 'Failed to update address');
  };

  // Delete address
  const deleteAddress = async (addressIndex) => {
    return await executeApiRequest(async () => {
      const { data } = await useFetch(`/api/user/address/${addressIndex}`, {
        method: 'DELETE',
      });
      userAddresses.value = data.value.data || [];
      return data.value.data;
    }, 'Failed to delete address');
  };

  // Get user exercises
  const getUserExercises = async () => {
    return await executeApiRequest(async () => {
      const { data } = await useFetch('/api/user/exercises');
      userExercises.value = data.value.data || [];
      return data.value.data;
    }, 'Failed to fetch exercises');
  };

  // Get user workouts
  const getUserWorkouts = async () => {
    return await executeApiRequest(async () => {
      const { data } = await useFetch('/api/user/workouts');
      userWorkouts.value = data.value.data || [];
      return data.value.data;
    }, 'Failed to fetch workouts');
  };

  // Add an exercise
  const addExercise = async (exerciseKey, pimExerciseId) => {
    return await executeApiRequest(async () => {
      const { data } = await useFetch('/api/user/exercise', {
        method: 'POST',
        body: { exerciseKey, pimExerciseId },
      });

      // Update the exercises in the shared state
      userExercises.value = data.value.data || [];

      // Update exercises in userDetails if available
      updateBlackrollCustomerProperty('exercises', data.value.data);

      return data.value.data;
    }, 'Failed to add exercise');
  };

  // Delete an exercise
  const deleteExercise = async (exerciseKey, pimExerciseId) => {
    return await executeApiRequest(async () => {
      const { data } = await useFetch(`/api/user/exercise/${exerciseKey}`, {
        method: 'DELETE',
        body: { pimExerciseId },
      });

      // Update the exercises in the shared state
      userExercises.value = data.value.data || [];

      // Update exercises in userDetails if available
      updateBlackrollCustomerProperty('exercises', data.value.data);

      return data.value.data;
    }, 'Failed to delete exercise');
  };

  // Add a workout
  const addWorkout = async (routineObjectId, pimId) => {
    return await executeApiRequest(async () => {
      const { data } = await useFetch('/api/user/workout', {
        method: 'POST',
        body: { routineObjectId, pimId },
      });

      // Update the workouts in the shared state
      userWorkouts.value = data.value.data || [];

      // Update workouts in userDetails if available
      updateBlackrollCustomerProperty('workouts', data.value.data);

      return data.value.data;
    }, 'Failed to add workout');
  };

  // Delete a workout
  const deleteWorkout = async (routineObjectId, pimId) => {
    return await executeApiRequest(async () => {
      const { data } = await useFetch(`/api/user/workout/${routineObjectId}`, {
        method: 'DELETE',
        body: { pimId },
      });

      // Update the workouts in the shared state
      userWorkouts.value = data.value.data || [];

      // Update workouts in userDetails if available
      updateBlackrollCustomerProperty('workouts', data.value.data);

      return data.value.data;
    }, 'Failed to delete workout');
  };

  // Clear all user data (e.g., on logout)
  const clearUserData = () => {
    userDetails.value = null;
    userOrders.value = [];
    userOrder.value = [];
    userAddresses.value = [];
    userExercises.value = [];
    userWorkouts.value = [];
  };

  return {
    // State
    userDetails,
    userOrders,
    userOrder,
    userAddresses,
    userExercises,
    userWorkouts,

    // Methods
    fetchUserDetails,
    fetchUserOrders,
    fetchUserOrder,
    fetchUserAddresses,
    updateUserProfile,
    updateAddress,
    deleteAddress,
    getUserExercises,
    getUserWorkouts,
    addExercise,
    deleteExercise,
    addWorkout,
    deleteWorkout,
    clearUserData,
  };
};
